import { memo, useState, useCallback, useEffect } from "react";
import { Grid, TextField, Typography, Box } from "@material-ui/core";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import LocalizationProvider from "@material-ui/lab/LocalizationProvider";
import DatePicker from "@material-ui/lab/DatePicker";
import { es } from "date-fns/locale";
import dayjs from "dayjs";

const DateRangeFilter = memo(
  ({
    id = "date-range-filter",
    label = "Filtrar por rango de fechas",
    fechaInicio,
    fechaFin,
    onChangeFechaInicio,
    onChangeFechaFin,
    inputFormat = "MM/yyyy",
    views = ["month", "year"],
    maxDate = null,
    minDate = null,
    tamanio = 12,
    required = false,
    ...other
  }) => {
    // Estados para el manejo de inputs manuales
    const [inputValueInicio, setInputValueInicio] = useState(
      fechaInicio ? dayjs(fechaInicio).format("MM/YYYY") : "",
    );
    const [inputValueFin, setInputValueFin] = useState(
      fechaFin ? dayjs(fechaFin).format("MM/YYYY") : "",
    );
    // Errores visuales cuando la entrada está completa pero es inválida
    const [errorInicio, setErrorInicio] = useState(false);
    const [errorFin, setErrorFin] = useState(false);

    // Sincroniza los inputs manuales cuando el padre cambia las props externamente
    // (ej: useEffect de Estadisticas.js hace setFechaFin(fechaInicio) para auto-ajustar).
    // Sin esto el input mostraría el valor anterior aunque el estado del padre cambió.
    useEffect(() => {
      setInputValueInicio(
        fechaInicio ? dayjs(fechaInicio).format("MM/YYYY") : "",
      );
      setErrorInicio(false);
    }, [fechaInicio]);

    useEffect(() => {
      setInputValueFin(fechaFin ? dayjs(fechaFin).format("MM/YYYY") : "");
      setErrorFin(false);
    }, [fechaFin]);

    // Handlers para fecha de inicio
    const handleInputChangeInicio = useCallback(
      (e) => {
        let rawValue = e.target.value.replace(/\D/g, "");
        if (rawValue.length > 6) rawValue = rawValue.slice(0, 6);

        let formattedValue = rawValue;
        if (rawValue.length >= 3) {
          formattedValue = `${rawValue.slice(0, 2)}/${rawValue.slice(2)}`;
        }

        setInputValueInicio(formattedValue);

        if (rawValue.length === 6) {
          const parsedDate = dayjs(formattedValue, "MM/YYYY", true); // strict mode
          if (parsedDate.isValid()) {
            onChangeFechaInicio(parsedDate.toDate());
            setErrorInicio(false);
          } else {
            // Entrada completa (6 dígitos) pero fecha inválida
            setErrorInicio(true);
            onChangeFechaInicio(null);
          }
        } else {
          // Entrada incompleta - no mostrar error
          setErrorInicio(false);
          onChangeFechaInicio(null);
        }
      },
      [onChangeFechaInicio],
    );

    const handlePickerChangeInicio = useCallback(
      (newValue) => {
        setInputValueInicio(newValue ? dayjs(newValue).format("MM/YYYY") : "");
        setErrorInicio(false);
        onChangeFechaInicio(newValue);
      },
      [onChangeFechaInicio],
    );

    // Handlers para fecha de fin
    const handleInputChangeFin = useCallback(
      (e) => {
        let rawValue = e.target.value.replace(/\D/g, "");
        if (rawValue.length > 6) rawValue = rawValue.slice(0, 6);

        let formattedValue = rawValue;
        if (rawValue.length >= 3) {
          formattedValue = `${rawValue.slice(0, 2)}/${rawValue.slice(2)}`;
        }

        setInputValueFin(formattedValue);

        if (rawValue.length === 6) {
          const parsedDate = dayjs(formattedValue, "MM/YYYY", true); // strict mode
          if (parsedDate.isValid()) {
            onChangeFechaFin(parsedDate.toDate());
            setErrorFin(false);
          } else {
            // Entrada completa (6 dígitos) pero fecha inválida
            setErrorFin(true);
            onChangeFechaFin(null);
          }
        } else {
          // Entrada incompleta - no mostrar error
          setErrorFin(false);
          onChangeFechaFin(null);
        }
      },
      [onChangeFechaFin],
    );

    const handlePickerChangeFin = useCallback(
      (newValue) => {
        setInputValueFin(newValue ? dayjs(newValue).format("MM/YYYY") : "");
        setErrorFin(false);
        onChangeFechaFin(newValue);
      },
      [onChangeFechaFin],
    );

    return (
      <Grid item xs={tamanio} {...other}>
        <Box>
          {label && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {label}
            </Typography>
          )}

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                <DatePicker
                  id={`${id}-inicio`}
                  label="Inicio"
                  value={fechaInicio}
                  onChange={handlePickerChangeInicio}
                  views={views}
                  disableMaskedInput
                  inputFormat={inputFormat}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required={required}
                      label="Inicio"
                      fullWidth
                      size="small"
                      value={inputValueInicio}
                      onChange={handleInputChangeInicio}
                      error={errorInicio}
                      helperText={
                        errorInicio
                          ? "Formato: MM/YYYY (Mes: 01-12, Año > 1900)"
                          : ""
                      }
                    />
                  )}
                  minDate={minDate}
                  maxDate={fechaFin || maxDate}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                <DatePicker
                  id={`${id}-fin`}
                  label="Fin"
                  value={fechaFin}
                  onChange={handlePickerChangeFin}
                  views={views}
                  disableMaskedInput
                  inputFormat={inputFormat}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required={required}
                      label="Fin"
                      fullWidth
                      size="small"
                      value={inputValueFin}
                      onChange={handleInputChangeFin}
                      error={errorFin}
                      helperText={
                        errorFin
                          ? "Formato: MM/YYYY (Mes: 01-12, Año > 1900)"
                          : ""
                      }
                    />
                  )}
                  minDate={fechaInicio || minDate}
                  maxDate={maxDate}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    );
  },
);

export default DateRangeFilter;
